import { FlowMEChangeContext } from "..";
import { useWSMutation, WSMutationConfig } from "../../../../../query/helpers";
import { useUserId } from "../../../../../query/hooks/helpers";
import {
  createCustomerEntity,
  getCustomerEntity,
  requestCustomerEntityChange,
  updateCustomerData
} from "../../../../../services/api/onboarding/customer";
import {
  getVerifications,
  performVerification
} from "../../../../../services/api/onboarding/verifications";
import { usersService } from "../../../../../services/users";
import { useFlowContext } from "../../../../../shared/components/Flow";
import { WSServiceError } from "../../../../../utils/serviceHelper";
import {
  getBusinessData,
  getIndividualData,
  getRepresentativeData,
  getUSPersonData
} from "../../../steps/CreateAccount/helpers";
import { VerificationState } from "../../../steps/CreateAccount/types";
import { getVerificationState } from "../../../steps/CreateAccount/useMutationSubmit";
import { useRequiresBankingVerification } from "../../../hooks/useRequiresBankingVerification";
import { selectorIsEntityVerified } from "../../../selectors/selectorIsEntityVerified";

export const useMutationSubmitMaterial = (
  config?: WSMutationConfig<VerificationState, WSServiceError, void>
) => {
  const userId = useUserId();
  const [context] = useFlowContext<FlowMEChangeContext>();
  const requiresBanking = useRequiresBankingVerification();

  return useWSMutation<VerificationState, WSServiceError, void>(async () => {
    if (!context.accountType?.country) {
      throw new Error("No country set");
    }

    const verificationLevel = requiresBanking ? "Banking" : "Tax";

    const user = await usersService.user.get(userId);

    const initialVerifications = await getVerifications();
    const isEntityVerified = selectorIsEntityVerified(initialVerifications);
    const currentEntity = await getCustomerEntity();
    const dataMatchesCurrentEntity =
      currentEntity.type === context.accountType.type &&
      currentEntity.country === context.accountType.country;

    let newEntityCreated = false;

    // 1. Request change
    if (context.accountType?.type === "Individual") {
      // 1.1. Request change for individual
      if (!context.personalInformation) {
        throw new Error("No personal information set");
      }

      const individualData = await getIndividualData({
        personFormData: context.personalInformation,
        user,
        verificationLevel,
        forceSetUserData: true
      });

      const usPersonData = getUSPersonData({
        personFormData: context.personalInformation,
        usPersonFormData: context.usInternationalPersonInformation
      });

      if (isEntityVerified) {
        // Current entity is verified, so we have to request a change instead of creating new entity
        await requestCustomerEntityChange({
          changeType: "Material",
          effectiveAt: context.reason?.effectiveAt,
          reason: context.reason?.reason,
          type: "Individual",
          country: context.accountType.country,
          customerData: { ...individualData, ...usPersonData }
        });
        newEntityCreated = true;
      } else {
        if (!dataMatchesCurrentEntity) {
          // Create a new entity if type and country don't match
          await createCustomerEntity({
            type: "Individual",
            country: context.accountType.country
          });
          newEntityCreated = true;
        }

        // Check verification status before submitting, to avoid overwriting
        // data that is already pending review or verified
        const preSubmitState = await getVerificationState(verificationLevel);

        const shouldSkipIndividualData =
          preSubmitState.status === "Pending" ||
          preSubmitState.status === "Verified";
        const shouldSkipUsPersonData =
          preSubmitState.usPersonStatus === "Pending" ||
          preSubmitState.usPersonStatus === "Verified";

        if (shouldSkipIndividualData && shouldSkipUsPersonData) {
          return preSubmitState;
        }

        await updateCustomerData("Entity", {
          customerData: {
            ...(shouldSkipIndividualData ? {} : individualData),
            ...(shouldSkipUsPersonData ? {} : usPersonData)
          }
        });
      }
    } else {
      // 1.2. Request change for business

      if (!context.businessInformation) {
        throw new Error("No business information set");
      }

      if (!context.representative) {
        throw new Error("No representative set");
      }

      // 1.2.1 Make change request
      const businessData = await getBusinessData(
        context.businessInformation,
        context.accountType.country,
        user
      );

      if (isEntityVerified) {
        // Current entity is verified, so we have to request a change instead of creating new entity
        await requestCustomerEntityChange({
          changeType: "Material",
          effectiveAt: context.reason?.effectiveAt,
          reason: context.reason?.reason,
          type: "Business",
          country: context.accountType.country,
          customerData: businessData
        });
        newEntityCreated = true;
      } else {
        if (!dataMatchesCurrentEntity) {
          // Create a new entity if type and country don't match
          await createCustomerEntity({
            type: "Business",
            country: context.accountType.country
          });
          newEntityCreated = true;
        }

        // Check verification status before submitting, to avoid overwriting
        // data that is already pending review or verified
        const preSubmitState = await getVerificationState(verificationLevel);

        if (
          preSubmitState.status === "Pending" ||
          preSubmitState.status === "Verified"
        ) {
          return preSubmitState;
        }

        await updateCustomerData("Entity", {
          customerData: businessData
        });
      }

      // 1.2.2 Follow up with update for representative
      const representativeData = await getRepresentativeData(
        context.representative,
        user,
        verificationLevel
      );

      await updateCustomerData("Representative", {
        customerData: representativeData
      });
    }

    // 2. Perform verification
    if (newEntityCreated) {
      await performVerification(verificationLevel);
    }

    // 3. Update cache
    return getVerificationState(verificationLevel);
  }, config);
};
